import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "@components/ui/Button";
import IconTrash from "@assets/icons/IconTrash";
import IconRefresh from "@assets/icons/IconRefresh";
import { sortTriangle } from "@assets/images";
import {
  loadConfig,
  deleteNewClients,
  setDeletedClients,
} from "@features/schema-management/actions";
import { recordChangeObject, logChangedKey } from "@actions/global";
import cloneDeep from "lodash/cloneDeep";
import { isEmptyObject } from "@utils/object-helpers";
import { logDiffs } from "@features/schema-management/utils/schemaManagementHelpers";
import { snakeToTitle } from "@utils/string-helpers";

const CustomSmArrayFieldTemplate = ({
  className,
  items,
  canAdd,
  onAddClick,
}) => {
  const featureName = useSelector((state) => state.schemaAllView.featureName);
  const validationErrors = useSelector(
    (state) => state.schemaAllView.validationErrors
  );
  const searchValue = useSelector((state) => state.schemaAllView.searchValue);
  const schemaManagementConfig = useSelector(
    (state) => state.schemaAllView.schemaManagementConfig
  );
  const initialConfig = useSelector(
    (state) => state.schemaAllView.initialConfig
  );
  const changeDict = useSelector((state) => state.schemaAllView.changeDict);
  const deletedClients = useSelector(
    (state) => state.schemaAllView.deletedClients
  );
  const newClients = useSelector((state) => state.schemaAllView.newClients);
  const [collapsed, setCollapsed] = useState(true);
  let deletedRefs = [];
  let isError = validationErrors && !isEmptyObject(validationErrors);
  const dispatch = useDispatch();

  useEffect(
    () =>
      deletedRefs.forEach((ref) =>
        document.getElementById(ref).classList.add("hidden")
      ),
    []
  );

  const getItemsToDisplay = (whereTosearch = "client") => {
    let updatedItems = items.map(
      (element, index) => items[items.length - 1 - index]
    );
    if (searchValue) {
      updatedItems = updatedItems.filter((element) =>
        element?.children?.props?.formData[whereTosearch]
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      );
    }
    return updatedItems;
  };

  const removeItem = (index, id) => {
    let updatedConfig = cloneDeep(schemaManagementConfig);
    updatedConfig[index] = initialConfig[index];
    const updatedDeletedClients = { ...deletedClients, [id]: null };
    const updatedChanges = {
      ...updatedDeletedClients,
      ...logDiffs(initialConfig, updatedConfig, featureName),
    };

    dispatch(loadConfig(updatedConfig));
    dispatch(setDeletedClients(updatedDeletedClients));
    dispatch(recordChangeObject(updatedChanges));
    dispatch(logChangedKey(Object.keys(updatedChanges)));
  };

  const undoItem = (id) => {
    let updatedChanges = cloneDeep(changeDict);
    delete updatedChanges[id];
    const { [id]: _, ...updatedDeletedClients } = deletedClients;

    dispatch(setDeletedClients(updatedDeletedClients));
    dispatch(recordChangeObject(updatedChanges));
    dispatch(logChangedKey(Object.keys(updatedChanges)));
  };

  const deleteNewClient = (element) => {
    element.onDropIndexClick(element.index)();
    let newClient = `root.${element.index}.client`;
    dispatch(deleteNewClients(newClient));
  };

  const isNewClient = (index) => {
    let newClient = `root.${index}.client`;
    return Object.keys(newClients).includes(newClient);
  };
  const isDeletedClient = (index, id) => {
    const isDeleted = Object.keys(deletedClients).includes(id);
    const featuresRef = document.getElementById(`root.${index}.features`);
    if (!featuresRef && isDeleted) deletedRefs.push(`root.${index}.features`);
    else
      isDeleted
        ? featuresRef?.classList.add("hidden")
        : featuresRef?.classList.remove("hidden");
    return !isDeleted;
  };

  let displayItems = getItemsToDisplay();
  const iconStyle = "absolute z-1001 right-0 mt-8 mr-2 w-33 cursor-pointer";

  return (
    <div className={className}>
      <div
        className={`override font-bold text-lg-2 shadow-lg bg-white my-2 rounded-md p-6 h-20 flex justify-between ${
          collapsed ? "hide-sibling" : ""
        }
          ${isError ? "!text-[#ff0000]" : ""}`}
      >
        {" "}
        <div className="flex">
          {`${snakeToTitle(featureName)} Config`}

          <img
            src={collapsed ? sortTriangle.up : sortTriangle.down}
            className="h-max my-auto mx-2 cursor-pointer"
            onClick={() => {
              setCollapsed(!collapsed);
            }}
          />
        </div>
        {canAdd && (
          <div className={"flex justify-end"}>
            <Button
              ariaLabel="+ Add Client"
              className="syf-login-action"
              toUpperCase={false}
              componentID="syf-login-action"
              label="+ Add Client"
              height="h-8"
              customStyle={
                " text-sm w-max pt-3 pr-5 inline-flex items-center justify-center m-0"
              }
              theme="primary"
              key="0"
              onClickCallback={() => {
                if (collapsed) setCollapsed(false);
                onAddClick();
              }}
              disabled={
                searchValue
                  ? true
                  : isError
                    ? Object.keys(validationErrors).some((validationError) =>
                        Object.keys(validationErrors[validationError]).some(
                          (error) => validationErrors[validationError][error]
                        )
                      )
                    : false
              }
            />
          </div>
        )}{" "}
      </div>

      {displayItems && displayItems.length ? (
        displayItems.map((element) => (
          <div
            key={element.key}
            className={`${element.className} flex relative`}
          >
            <div className="w-full">{element.children}</div>

            {element.hasRemove &&
              (isDeletedClient(
                element.index,
                element.children.props.formData?.client
              ) ? (
                <IconTrash
                  iconFillColor="#34657f"
                  className={iconStyle}
                  onClick={() => {
                    if (
                      element.children.props.formData?.client &&
                      !isNewClient(element.index)
                    )
                      removeItem(
                        element.index,
                        element.children.props.formData?.client
                      );
                    else {
                      deleteNewClient(element);
                    }
                  }}
                />
              ) : (
                <IconRefresh
                  iconFillColor="#34657f"
                  className={iconStyle}
                  onClick={() => {
                    undoItem(element.children.props.formData?.client);
                  }}
                />
              ))}
          </div>
        ))
      ) : (
        <div
          className={`flex justify-center items-center nter h-[70vh] w-[70vw] title-primary
          `}
        >
          <span>
            Config Is Empty. Please Click On "+ Add Client" To Add A New Client.
          </span>
        </div>
      )}
    </div>
  );
};

export default CustomSmArrayFieldTemplate;
